import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import { trackPage } from '~/components/analytics/analytics';

import type { ReactElement, ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

const PREVIOUS_URL_STORAGE_KEY = 'analytics.previousUrl';

const getStoredPreviousUrl = (): string => {
  try {
    return window.sessionStorage.getItem(PREVIOUS_URL_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const clearStoredPreviousUrl = (): void => {
  try {
    window.sessionStorage.removeItem(PREVIOUS_URL_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
};

const storeCurrentUrl = (url: string): void => {
  try {
    window.sessionStorage.setItem(PREVIOUS_URL_STORAGE_KEY, url);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
};

const getNavigationType = (): PerformanceNavigationTiming['type'] | undefined => {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return undefined;
  }

  const [navigationEntry] = performance.getEntriesByType(
    'navigation'
  ) as PerformanceNavigationTiming[];

  return navigationEntry?.type;
};

const AnalyticsPageTracker = ({ children }: Props): ReactElement => {
  const isMounted = useRef<boolean>(false);
  const lastVisitedPathname = useRef<string>('');
  const currentUrl = useRef<string>('');
  const previousUrl = useRef<string>('');

  const router = useRouter();

  const handleRouteChangeStart = useCallback((): void => {
    if (typeof window !== 'undefined') {
      previousUrl.current = currentUrl.current || window.location.href;
    }
  }, []);

  const handleRouteChangeComplete = useCallback((url: string): void => {
    const [pathname] = url.split('?');
    const referrer =
      previousUrl.current ||
      currentUrl.current ||
      (typeof document !== 'undefined' ? document.referrer : '');

    if (typeof window !== 'undefined') {
      currentUrl.current = new URL(url, window.location.origin).href;
    }

    if (pathname === lastVisitedPathname.current) {
      previousUrl.current = '';
      return;
    }

    lastVisitedPathname.current = pathname;
    trackPage(referrer || undefined, url);
    previousUrl.current = '';
  }, []);

  const handlePageHide = useCallback((): void => {
    if (typeof window !== 'undefined') {
      storeCurrentUrl(currentUrl.current || window.location.href);
    }
  }, []);

  const handlePageShow = useCallback((event: PageTransitionEvent): void => {
    if (!event.persisted || typeof window === 'undefined') {
      return;
    }

    const restoredFromUrl = getStoredPreviousUrl();
    currentUrl.current = window.location.href;
    lastVisitedPathname.current = window.location.pathname;
    clearStoredPreviousUrl();

    if (restoredFromUrl && restoredFromUrl !== currentUrl.current) {
      trackPage(restoredFromUrl, currentUrl.current);
    }
  }, []);

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return (): void => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [
    handlePageHide,
    handlePageShow,
    handleRouteChangeComplete,
    handleRouteChangeStart,
    router.events,
  ]);

  useEffect(() => {
    if (isMounted.current) {
      return;
    }

    isMounted.current = true;

    lastVisitedPathname.current = window.location.pathname;
    currentUrl.current = window.location.href;

    const storedPreviousUrl = getStoredPreviousUrl();
    clearStoredPreviousUrl();
    const historyReferrer =
      getNavigationType() === 'back_forward' && storedPreviousUrl !== currentUrl.current
        ? storedPreviousUrl
        : '';

    trackPage(historyReferrer || document.referrer || undefined, currentUrl.current);
  }, []);

  return <>{children}</>;
};

export { AnalyticsPageTracker };
